/**
 * Client-Side Pricing Service
 * 🔒 SECURE: No business logic exposed - all calculations done on server
 */

import config from '../config';

class PricingService {
  /**
   * Calculate price using secure server-side API
   * 🔒 Business logic hidden on server
   */
  static async calculatePrice(basePrice, channel, adType, startDate, endDate, quantity = 1, customerId = null, token = null, addons = {}) {
    try {
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      const response = await fetch(`${config.apiUrl}/api/pricing/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          basePrice,
          channel,
          adType,
          startDate,
          endDate,
          quantity,
          customerId,
          addons
        })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Pricing calculation failed');
      }
      
      return result;
    } catch (error) {
      console.error('Pricing calculation error:', error);
      return {
        success: false,
        message: error.message,
        price: basePrice // Fallback to base price
      };
    }
  }
  
  /**
   * Get available channels for organization
   * 🔒 Business logic hidden on server
   */
  static async getAvailableChannels(organizationId) {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      const response = await fetch(`${config.apiUrl}/api/pricing/channels/${organizationId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to get channels');
      }
      
      return result;
    } catch (error) {
      console.error('Channel lookup error:', error);
      return {
        success: false,
        message: error.message,
        channels: []
      };
    }
  }
  
  /**
   * Get pricing rules (admin only)
   * 🔒 Only accessible to admin users
   */
  static async getPricingRules() {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      const response = await fetch(`${config.apiUrl}/api/pricing/rules`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to get pricing rules');
      }
      
      return result;
    } catch (error) {
      console.error('Pricing rules error:', error);
      return {
        success: false,
        message: error.message,
        pricingRules: null
      };
    }
  }
  
  /**
   * Update pricing rule (admin only)
   * 🔒 Only accessible to admin users
   */
  static async updatePricingRule(channel, adType, multiplier) {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      const response = await fetch(`${config.apiUrl}/api/pricing/rules`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          channel,
          adType,
          multiplier
        })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to update pricing rule');
      }
      
      return result;
    } catch (error) {
      console.error('Pricing rule update error:', error);
      return {
        success: false,
        message: error.message
      };
    }
  }
  
  /**
   * Calculate total for multiple items
   * 🔒 Uses secure server-side calculations
   */
  static async calculateTotal(items) {
    try {
      const calculations = await Promise.all(
        items.map(item => 
          this.calculatePrice(
            item.basePrice,
            item.channel,
            item.adType,
            item.quantity || 1,
            item.customerId
          )
        )
      );
      
      const total = calculations.reduce((sum, calc) => {
        return sum + (calc.success ? calc.price : parseFloat(calc.basePrice || 0));
      }, 0);
      
      return {
        success: true,
        total: parseFloat(total.toFixed(2)),
        itemCalculations: calculations
      };
    } catch (error) {
      console.error('Total calculation error:', error);
      return {
        success: false,
        message: error.message,
        total: 0
      };
    }
  }
  
  /**
   * Validate pricing data on client side (for UX only)
   * ⚠️ Note: Server-side validation is still required for security
   */
  static validatePricingData(basePrice, channel, adType) {
    const errors = [];
    
    if (!basePrice || isNaN(parseFloat(basePrice)) || parseFloat(basePrice) <= 0) {
      errors.push('Valid base price is required');
    }
    
    if (!channel || channel.trim() === '') {
      errors.push('Channel is required');
    }
    
    if (!adType || adType.trim() === '') {
      errors.push('Ad type is required');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
  
  /**
   * Format price for display
   */
  static formatPrice(price, currency = 'AUD') {
    if (price === null || price === undefined || isNaN(price)) {
      return 'N/A';
    }
    
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  }
  
  /**
   * Get price breakdown explanation
   */
  static formatPriceBreakdown(breakdown) {
    if (!breakdown) return '';
    
    const parts = [];
    
    if (breakdown.basePrice) {
      parts.push(`Base: ${this.formatPrice(breakdown.basePrice)}`);
    }
    
    if (breakdown.multiplier && breakdown.multiplier !== 1) {
      parts.push(`Multiplier: ${breakdown.multiplier}x`);
    }
    
    if (breakdown.bulkDiscount && breakdown.bulkDiscount > 0) {
      parts.push(`Bulk Discount: ${(breakdown.bulkDiscount * 100).toFixed(1)}%`);
    }
    
    if (breakdown.loyaltyDiscount && breakdown.loyaltyDiscount > 0) {
      parts.push(`Loyalty Discount: ${(breakdown.loyaltyDiscount * 100).toFixed(1)}%`);
    }
    
    if (breakdown.seasonalMultiplier && breakdown.seasonalMultiplier !== 1) {
      const percentage = ((breakdown.seasonalMultiplier - 1) * 100).toFixed(1);
      const label = breakdown.seasonalMultiplier > 1 ? 'Seasonal Surcharge' : 'Seasonal Discount';
      parts.push(`${label}: ${Math.abs(percentage)}%`);
    }
    
    return parts.join(' | ');
  }

  /**
   * Check if top spot is available for a school and channel
   * @param {number} organizationId - School organization ID
   * @param {string} channel - Channel (e.g., 'ePublisher')
   * @param {string} startDate - Start date (optional)
   * @param {string} endDate - End date (optional)
   * @returns {Promise<Object>} - Availability result
   */
  static async checkTopSpotAvailability(organizationId, channel, startDate = null, endDate = null) {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      
      const requestBody = {
        organizationId,
        channel
      };
      
      // Include dates if provided
      if (startDate && endDate) {
        requestBody.startDate = startDate;
        requestBody.endDate = endDate;
      }
      
      const response = await fetch(`${config.apiUrl}/api/pricing/check-top-spot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Top spot availability check failed');
      }

      return result;
    } catch (error) {
      console.error('Top spot availability check error:', error);
      throw error;
    }
  }
}

export default PricingService; 